// ─────────────────────────────────────────────────────────────────────────────
// GENERATED FILE — DO NOT EDIT HERE.
//
// Source of truth: invits-app-new/shared/effectPlayer.js
// Regenerate:     npm run sync:shared   (from invits-app-new)
//
// Editing this copy reintroduces exactly the app/web divergence that shared/
// exists to eliminate. Change the source, then re-run the sync.
// ─────────────────────────────────────────────────────────────────────────────

// shared/effectPlayer.js
//
// The page that plays an invitation effect: a stacked-alpha MP4 drawn through
// WebGL. One page for every client — the app shows it in a transparent
// WebView, the website in a transparent iframe — so an effect looks and
// behaves the same everywhere.
//
// ── The format ─────────────────────────────────────────────────────────────
//
// The file is an ordinary H.264 video twice as tall as the picture: colour in
// the top half, transparency as a greyscale mask in the bottom half (see
// scripts/encode-effects.sh). The fragment shader samples both halves at the
// same point and outputs the colour with the mask as its alpha.
//
// Why: there is no transparent video format every device plays. H.264 is
// hardware-decoded on every phone and browser, and the recombining is one
// texture upload and a two-line shader on the GPU — smooth, and light on the
// CPU, where Android decodes WebM alpha in software.
//
// ── Cover ──────────────────────────────────────────────────────────────────
//
// Scaled to fill the screen and cropped, centred, like CSS object-fit: cover,
// computed in the vertex stage so no pixels are wasted.
//
// Kept import-free and as a plain string builder so the app (Hermes cannot
// stringify functions) and the website share the exact same code.

/** Minimal escaping for a URL placed inside a double-quoted JS string. */
function jsString(value) {
  return JSON.stringify(String(value)).replace(/</g, '\\u003c');
}

/**
 * @param {string} url  stacked-alpha MP4
 * @returns {string} a complete HTML document
 */
export function effectPlayerHtml(url) {
  return `<!doctype html><html><head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=1">
<style>
html,body{margin:0;width:100%;height:100%;overflow:hidden;background:transparent}
/* The video stays on screen, full size, under the canvas at 1% opacity.
   WebKit (iPhone apps, Instagram's browser, Safari) stops advancing frames of
   a video it considers invisible — a 1px or opacity:0 video froze on its
   first frame. At 1% the stacked file is imperceptible under the canvas. */
video{position:fixed;inset:0;width:100%;height:100%;object-fit:cover;opacity:.01;pointer-events:none}
canvas{position:fixed;inset:0;width:100%;height:100%;display:block;opacity:0;transition:opacity .6s ease}
canvas.on{opacity:1}
/* No play button, ever. When autoplay is refused (an iPhone in Low Power
   Mode) WebKit draws one over the video, and even at 1% it showed as a faint
   glyph on the invitation. A blocked effect should simply not be there. */
video::-webkit-media-controls,video::-webkit-media-controls-start-playback-button,
video::-webkit-media-controls-overlay-play-button{display:none!important;-webkit-appearance:none}
</style></head><body>
<!-- The poster is a transparent pixel: without one, Android draws a grey
     play icon until the first frame arrives. -->
<video id="v" muted loop playsinline webkit-playsinline autoplay crossorigin="anonymous" preload="auto"
  poster="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"></video>
<canvas id="c"></canvas>
<script>
(function () {
  var video = document.getElementById('v');
  var canvas = document.getElementById('c');
  // Tells whoever shows this page how playback went, once per kind:
  // 'playing', or 'failed' with a reason. The app's web view hears it through
  // ReactNativeWebView, the website through the iframe's parent - both to
  // switch to the Low Power version when autoplay is refused.
  var told = {};
  function report(status, reason) {
    var key = status + ':' + (reason || '');
    if (told[key]) return;
    told[key] = true;
    var msg = { type: 'effect', source: 'invits-effect', status: status, reason: reason || null };
    try { if (window.ReactNativeWebView) window.ReactNativeWebView.postMessage(JSON.stringify(msg)); } catch (e) {}
    try { if (window.parent && window.parent !== window) window.parent.postMessage(msg, '*'); } catch (e) {}
  }

  var gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: true, antialias: false, powerPreference: 'low-power' })
        || canvas.getContext('experimental-webgl', { alpha: true, premultipliedAlpha: true });
  if (!gl) { report('failed', 'no-webgl'); return; }

  function shader(type, src) {
    var s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); return s;
  }
  var prog = gl.createProgram();
  gl.attachShader(prog, shader(gl.VERTEX_SHADER,
    'attribute vec2 p; uniform vec2 scale; varying vec2 uv;' +
    'void main(){ uv = p * 0.5 / scale + 0.5; uv.y = 1.0 - uv.y; gl_Position = vec4(p, 0.0, 1.0); }'));
  gl.attachShader(prog, shader(gl.FRAGMENT_SHADER,
    'precision mediump float; uniform sampler2D t; varying vec2 uv;' +
    'void main(){' +
    '  vec3 rgb = texture2D(t, vec2(uv.x, uv.y * 0.5)).rgb;' +
    '  float a = texture2D(t, vec2(uv.x, 0.5 + uv.y * 0.5)).r;' +
    '  a = clamp((a - 0.03) / 0.97, 0.0, 1.0);' +
    '  gl_FragColor = vec4(rgb * a, a);' +
    '}'));
  gl.linkProgram(prog);
  gl.useProgram(prog);

  var buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW);
  var loc = gl.getAttribLocation(prog, 'p');
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  var scaleLoc = gl.getUniformLocation(prog, 'scale');

  var tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.clearColor(0, 0, 0, 0);

  // Sized once per WIDTH. Mobile browsers change the height as the address
  // bar slides on scroll; resizing then re-cropped the effect and made it
  // jump. Height changes alone are ignored. Resolution is capped: the effect
  // is soft, and full retina resolution was what made it lag on desktops.
  var sizedWidth = -1;
  function resize(force) {
    var cssW = canvas.clientWidth, cssH = canvas.clientHeight;
    if (!force && cssW === sizedWidth) return;
    sizedWidth = cssW;
    var scale = Math.min(window.devicePixelRatio || 1, 1.5);
    var w = Math.min(Math.round(cssW * scale), 1280);
    var h = Math.round(w * cssH / Math.max(cssW, 1));
    canvas.width = w; canvas.height = h;
    gl.viewport(0, 0, w, h);
    if (!video.videoWidth) return;
    var videoAspect = video.videoWidth / (video.videoHeight / 2);
    var viewAspect = w / h;
    gl.uniform2f(scaleLoc,
      viewAspect > videoAspect ? 1 : videoAspect / viewAspect,
      viewAspect > videoAspect ? viewAspect / videoAspect : 1);
  }

  // Shown only while frames are actually advancing: faded in after the
  // second new frame, faded out if playback stalls. Never a frozen overlay.
  var lastTime = -1, framesSeen = 0, lastAdvance = 0;
  function tick(now) {
    if (!document.hidden) {
      if (video.readyState >= 2 && video.currentTime !== lastTime) {
        lastTime = video.currentTime;
        lastAdvance = now;
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, video);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        if (++framesSeen === 2) { canvas.className = 'on'; report('playing'); }
      } else if (framesSeen >= 2 && now - lastAdvance > 1500) {
        canvas.className = '';
        framesSeen = 0;
        tryPlay();
      }
    }
    requestAnimationFrame(tick);
  }

  // NotAllowedError is the browser refusing to autoplay - on an iPhone, Low
  // Power Mode, which no setting of ours can override. Anything else is a
  // load or decode problem.
  function tryPlay() {
    var p = video.play();
    if (p && p.catch) p.catch(function (e) {
      report('failed', e && e.name === 'NotAllowedError' ? 'autoplay-blocked' : 'play-' + ((e && e.name) || 'error'));
    });
  }

  window.addEventListener('resize', function () { resize(false); });
  video.addEventListener('loadedmetadata', function () { resize(true); });
  // A play() before there was anything to play can be dropped quietly;
  // asked again once there is.
  video.addEventListener('canplay', function () { if (video.paused) tryPlay(); });
  video.addEventListener('error', function () {
    report('failed', 'media-error-' + ((video.error && video.error.code) || 0));
  });
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) video.pause(); else tryPlay();
  });

  video.src = ${jsString(url)};
  tryPlay();
  requestAnimationFrame(tick);
})();
</script></body></html>`;
}

/** Which encoding to play: the portrait file on a portrait screen. */
export function effectUrlFor(effect, { width, height }) {
  if (!effect) return null;
  const portrait = height >= width;
  return (portrait ? effect.mobile_url : effect.web_url) || effect.mobile_url || effect.web_url || null;
}
